import { useQuery } from 'react-query'
import { useAuth0 } from '../../../contexts/auth0-context'
import endpoints from '../../../API/endpoints'
import callAPI from '../../../API/callSecureAPI'
import { ONE_MINUTE } from '../../constants/timing'
import { LinkedItems, LinkingItem, ROADMAP_SHARED_LINK_TYPES } from '../../../components/Linking/Linking.types'

// roadmapId is required for a roadmap-shared type (see ROADMAP_SHARED_LINK_TYPES), so the
// backend can scope the response to this roadmap's own links. Ignored for every other type.
export const useGetLinkedItemsQuery = (id: LinkingItem['_id'], type: LinkingItem['type'], roadmapId?: string) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()
    const isRoadmapShared = ROADMAP_SHARED_LINK_TYPES.includes(type)

    const getEndpointByType = () => {
        switch (type) {
            case 'activity':
                return endpoints.getActivityLinkedItems
            case 'evidence':
                return endpoints.getEvidenceLinkedItems
            case 'valueMessage':
                return endpoints.getValueMessageLinkedItems
            case 'strategicImperative':
                return endpoints.getStrategicImperativeLinkedItems
            case 'tppCell':
                return endpoints.getTppCellLinkedItems
            default:
                return null
        }
    }

    const getLinkedItems = async () => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            const endpoint = getEndpointByType()
            if (!endpoint) return null

            const url = isRoadmapShared ? `${endpoint.url}${id}/roadmap/${roadmapId}` : endpoint.url + id

            const response = await callAPI(url, endpoint.method, token)

            return response.data as LinkedItems
        } catch (e) {
            console.log(e)
        }
    }

    // Only a roadmap-shared type carries roadmapId in the key. Other types must keep their
    // original 3-element key, since some callers read it back with an exact match.
    const queryKey = isRoadmapShared ? ['linkedItems', id, type, roadmapId] : ['linkedItems', id, type]

    return useQuery<LinkedItems>(queryKey, getLinkedItems, {
        enabled:
            !!getTokenSilently &&
            !!isAuthenticated &&
            !isLoading &&
            !!id &&
            !!type &&
            (!isRoadmapShared || !!roadmapId),

        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,

        cacheTime: ONE_MINUTE,
        staleTime: ONE_MINUTE,
    })
}
